import React from "react";
import * as TablerIcons from "@tabler/icons-react";
import styles from "./MInputField.module.css";

export type MInputFieldState =
  | "Default"
  | "Focused"
  | "Entered"
  | "Error"
  | "Success"
  | "Disabled";
export type MInputFieldSize = "md" | "xl";
export type MInputFieldVariant = "Outlined" | "Underlined" | "SummaryDisplay";

export type MInputFieldProps = {
  clearInput?: boolean;
  isDrawerInput?: boolean; // new boolean prop
  labelClassName?: string;
  dialog?: boolean;
  /** Optional click handler for the icon (e.g. clear button) */
  onIconClick?: () => void;
  /** Maximum number of characters allowed in the input */
  maxLength?: number;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  state?: MInputFieldState;
  size?: MInputFieldSize;
  variant?: MInputFieldVariant;
  showPlaceholder?: boolean;
  placeholder?: string;
  showIcon?: boolean;
  icon?: string | React.ReactNode;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  stateMessage?: string;
  messageIcon?: React.ReactNode;
  label?: string;
  showLabel?: boolean;
  showSIUnit?: boolean;
  siUnit?: string;
  mandatory?: boolean;
  type?: string;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  autoFocus?: boolean;
  className?: string;
  style?: React.CSSProperties;
  inputMode?:
    | "text"
    | "numeric"
    | "decimal"
    | "tel"
    | "email"
    | "search"
    | "url";
};

export const MInputField: React.FC<MInputFieldProps> = ({
  clearInput = false,
  isDrawerInput = false,
  dialog = false,
  state = "Default",
  size = "md",
  variant = "Underlined",
  showPlaceholder = true,
  placeholder = "",
  showIcon = false,
  icon,
  value,
  onChange,
  onClick,
  stateMessage = "",
  label = "",
  showLabel = true,
  showSIUnit = false,
  siUnit = "",
  mandatory = false,
  type = "text",
  disabled = false,
  readOnly = false,
  name,
  autoFocus = false,
  className = "",
  style,
  onFocus,
  onBlur,
  maxLength,
  onIconClick,
  labelClassName,
  inputMode,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [showStateMsg, setShowStateMsg] = React.useState(!!stateMessage);
  const [fade, setFade] = React.useState(false);
  //useFixMobileKeyboard();

  React.useEffect(() => {
    if (stateMessage) {
      setShowStateMsg(true);
      setFade(false);
      const fadeTimer = setTimeout(() => setFade(true), 2000);
      const hideTimer = setTimeout(() => setShowStateMsg(false), 2500);
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    } else {
      setShowStateMsg(false);
    }
  }, [stateMessage]);

  const getInputWrapperStyle = (): React.CSSProperties | undefined => {
    if (dialog) {
      return {
        padding: 0,
        height: 40,
        borderBottom:
          "0.2px solid var(--brand-colours-indigo-indigo-300) !important",
      };
    }
    if (isDrawerInput) {
      return { height: 40 };
    }
    return undefined;
  };

  return (
    <div
      className={[
        styles.minputfieldRoot,
        variant === "Outlined" ? styles.outline : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
    >
      {showLabel && label && (
        <div
          className={[
            styles.minputfieldLabel,
            variant === "SummaryDisplay" ? styles.summaryDisplayLabel : "",
            isDrawerInput ? styles.drawerLabel : "",
            labelClassName,
            isDrawerInput
              ? "body-base-medium neutral-color relativePosition"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={dialog ? { fontSize: 12, fontWeight: 500 } : undefined}
          onClick={() => inputRef.current?.focus()}
        >
          {label}
          {mandatory && (
            <span className={styles.minputfieldLabelAsterisk}>*</span>
          )}
        </div>
      )}

      <div
        className={[
          styles.minputfieldInputWrapper,
          variant === "Underlined" || variant === "SummaryDisplay"
            ? styles.underlined
            : "",
          variant === "Outlined" ? styles.outline : "",
          size && styles[size],
          state?.toLowerCase() &&
            styles[state.toLowerCase() as keyof typeof styles],
          disabled ? styles.disabled : "",
          isDrawerInput && variant === "Underlined"
            ? "underlined md drawer-grey"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={getInputWrapperStyle()}
      >
        <input
          className={[
            styles.minputfieldInput,
            size === "xl" ? "text-xl" : "",
            isDrawerInput ? "body-base-medium neutral-color" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          ref={inputRef}
          type={type}
          inputMode={inputMode}
          pattern={
            inputMode === "numeric" || inputMode === "tel"
              ? "[0-9]*"
              : undefined
          }
          value={value}
          onChange={onChange}
          onClick={onClick}
          placeholder={showPlaceholder ? placeholder : ""}
          disabled={disabled}
          readOnly={readOnly}
          name={name}
          autoFocus={autoFocus}
          onFocus={onFocus}
          onBlur={onBlur}
          maxLength={maxLength}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur(); // hides keyboard on Android & iOS
              // Optional: trigger parent callback if needed
              if (typeof onBlur === "function") onBlur(e as any);
            }
          }}
          style={{
            ...(value === "Not Answered"
              ? { color: "var(--brand-colours-indigo-indigo-200, #AD95D1)" }
              : {}),
            ...(isDrawerInput
              ? { color: "var(--colour-text-default, #2A2A2A)" }
              : {}),
            ...(clearInput ? { marginRight: "10%" } : {}),
          }}
        />
        {showSIUnit && siUnit && (
          <span className={styles.minputfieldSiu}>{siUnit}</span>
        )}
        {showIcon && (
          <span className={styles.minputfieldIcon}>
            {onIconClick ? (
              <span
                style={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={onIconClick}
              >
                {(() => {
                  if (!icon) return <TablerIcons.IconEye size={16} />;
                  if (typeof icon === "string") {
                    const iconName =
                      "Icon" +
                      icon
                        .split("-")
                        .map(
                          (part) => part.charAt(0).toUpperCase() + part.slice(1)
                        )
                        .join("");
                    const IconComponent = (TablerIcons as any)[iconName];
                    if (IconComponent) return <IconComponent size={16} />;
                    return <TablerIcons.IconEye size={16} />;
                  }
                  // If it's a ReactNode, force size to 16x16
                  return (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 16,
                        height: 16,
                      }}
                    >
                      {React.isValidElement(icon)
                        ? React.cloneElement(icon as React.ReactElement, {
                            width: 16,
                            height: 16,
                            size: 16,
                          })
                        : icon}
                    </span>
                  );
                })()}
              </span>
            ) : (
              (() => {
                if (!icon) return <TablerIcons.IconEye size={16} />;
                if (typeof icon === "string") {
                  const iconName =
                    "Icon" +
                    icon
                      .split("-")
                      .map(
                        (part) => part.charAt(0).toUpperCase() + part.slice(1)
                      )
                      .join("");
                  const IconComponent = (TablerIcons as any)[iconName];
                  if (IconComponent) return <IconComponent size={16} />;
                  return <TablerIcons.IconEye size={16} />;
                }
                return icon;
              })()
            )}
          </span>
        )}
      </div>
      {variant === "Underlined" && stateMessage && showStateMsg && (
        <div
          className={[
            styles.minputfieldStateMessage,
            state === "Error" ? styles.error : "",
            state === "Success" ? styles.success : "",
            fade ? styles.fade : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            fontSize: 16,
            transition: "opacity 0.4s",
            opacity: fade ? 0 : 1,
          }}
        >
          {state === "Error" && (
            <span style={{ display: "flex", alignItems: "center" }}>
              <TablerIcons.IconAlertSquareRoundedFilled
                size={16}
                color="#C33F58"
                style={{ display: "block" }}
              />
            </span>
          )}
          {state === "Success" && (
            <span style={{ display: "flex", alignItems: "center" }}>
              <TablerIcons.IconSquareRoundedCheckFilled
                size={16}
                color="#217B57"
                style={{ display: "block" }}
              />
            </span>
          )}
          <span style={{ marginLeft: 8 }}>{stateMessage}</span>
        </div>
      )}
      {variant === "Outlined" && stateMessage && showStateMsg && (
        <div
          className={[
            styles.minputfieldStateMessage,
            state === "Error" ? styles.error : "",
            state === "Success" ? styles.success : "",
            fade ? styles.fade : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            fontSize: 20,
            transition: "opacity 0.4s",
            opacity: fade ? 0 : 1,
          }}
        >
          {state === "Error" && (
            <span style={{ display: "flex", alignItems: "center" }}>
              <TablerIcons.IconAlertSquareRoundedFilled
                size={20}
                color="#C33F58"
              />
            </span>
          )}
          {state === "Success" && (
            <span style={{ display: "flex", alignItems: "center" }}>
              <TablerIcons.IconSquareRoundedCheckFilled
                size={20}
                color="#217B57"
              />
            </span>
          )}
          <span style={{ marginLeft: 8 }}>{stateMessage}</span>
        </div>
      )}
    </div>
  );
};

export default MInputField;
